import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
    fetchProjectTimelogWorkload,
    fetchUserTimelogWorkload,
    fetchWorkloadScreenshotsList,
} from './workload.service'

const initialScreenshotsState = {
    status: 'idle',
    error: '',
    rows: [],
    skip: 0,
    limit: 20,
    isNext: false,
}

const initialDetailState = {
    open: false,
    status: 'idle',
    error: '',
    user: null,
    rows: [],
    screenshots: initialScreenshotsState,
}

const initialState = {
    status: 'idle',
    error: '',
    rows: [],
    skip: 0,
    limit: 10,
    search: '',
    start: null,
    end: null,
    periodType: 'day',
    projectIds: [],
    userIds: [],
    totalCount: 0,
    totalLog: 0,
    isNext: false,
    detail: initialDetailState,
}

export const fetchWorkloadUserTimelogList = createAsyncThunk(
    'workload/fetchUserTimelogList',
    async ({ reset = false } = {}, { getState, rejectWithValue }) => {
        const { workloadSlice } = getState()
        const skip = reset ? 0 : workloadSlice?.skip || 0

        try {
            const response = await fetchUserTimelogWorkload({
                skip,
                limit: workloadSlice?.limit ?? 10,
                search: workloadSlice?.search ?? '',
                start: workloadSlice?.start,
                end: workloadSlice?.end,
                projectId: workloadSlice?.projectIds ?? [],
                userId: workloadSlice?.userIds ?? [],
            })

            const body = response?.data ?? {}
            return {
                reset,
                requestSkip: skip,
                data: body,
            }
        } catch (error) {
            return rejectWithValue(
                error?.response?.data?.message ||
                    error?.message ||
                    'Failed to load workload data'
            )
        }
    }
)

export const fetchWorkloadProjectTimelog = createAsyncThunk(
    'workload/fetchProjectTimelog',
    async (_, { getState, rejectWithValue }) => {
        const { workloadSlice } = getState()
        const userId = workloadSlice?.detail?.user?._id

        if (!userId) {
            return rejectWithValue('No team member selected')
        }

        try {
            const response = await fetchProjectTimelogWorkload({
                start: workloadSlice?.start,
                end: workloadSlice?.end,
                projectId: workloadSlice?.projectIds ?? [],
                userId,
            })

            return response?.data ?? {}
        } catch (error) {
            return rejectWithValue(
                error?.response?.data?.message ||
                    error?.message ||
                    'Failed to load timelog details'
            )
        }
    }
)

export const fetchWorkloadScreenshots = createAsyncThunk(
    'workload/fetchScreenshots',
    async ({ reset = true } = {}, { getState, rejectWithValue }) => {
        const { workloadSlice } = getState()
        const userId = workloadSlice?.detail?.user?._id

        if (!userId) {
            return rejectWithValue('No team member selected')
        }

        const screenshots = workloadSlice?.detail?.screenshots
        const skip = reset ? 0 : screenshots?.skip || 0
        const limit = screenshots?.limit ?? 20

        try {
            const response = await fetchWorkloadScreenshotsList({
                skip,
                limit,
                start: workloadSlice?.start,
                end: workloadSlice?.end,
                projectId: workloadSlice?.projectIds ?? [],
                userId,
                assigneeId: [userId],
            })

            return {
                reset,
                data: response?.data ?? {},
            }
        } catch (error) {
            return rejectWithValue(
                error?.response?.data?.message ||
                    error?.message ||
                    'Failed to load screenshots'
            )
        }
    }
)

const workloadSlice = createSlice({
    name: 'workloadSlice',
    initialState,
    reducers: {
        setWorkloadFilters: (state, action) => {
            const {
                search,
                start,
                end,
                periodType,
                projectIds,
                userIds,
                limit,
            } = action.payload ?? {}
            if (search !== undefined) state.search = search
            if (start !== undefined) state.start = start
            if (end !== undefined) state.end = end
            if (periodType !== undefined) state.periodType = periodType
            if (projectIds !== undefined) state.projectIds = projectIds
            if (userIds !== undefined) state.userIds = userIds
            if (limit !== undefined) state.limit = limit
            state.skip = 0
        },
        openWorkloadTimelogDrawer: (state, action) => {
            state.detail = {
                open: true,
                status: 'loading',
                error: '',
                user: action.payload ?? null,
                rows: [],
                screenshots: { ...initialScreenshotsState, status: 'loading' },
            }
        },
        closeWorkloadTimelogDrawer: (state) => {
            state.detail = initialDetailState
        },
        resetWorkloadState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWorkloadUserTimelogList.pending, (state, action) => {
                state.status =
                    action.meta?.arg?.reset === false
                        ? 'loadingMore'
                        : 'loading'
                state.error = ''
            })
            .addCase(
                fetchWorkloadUserTimelogList.fulfilled,
                (state, action) => {
                    state.status = 'loaded'
                    const { reset, data } = action.payload ?? {}
                    const list = Array.isArray(data?.data) ? data.data : []

                    state.rows = reset ? list : [...state.rows, ...list]
                    state.skip = state.rows.length
                    state.totalCount = Number(data?.totalUsers) || 0
                    state.totalLog = Number(data?.totalLog) || 0
                    state.isNext = Boolean(data?.isNext)
                }
            )
            .addCase(
                fetchWorkloadUserTimelogList.rejected,
                (state, action) => {
                    state.status = 'error'
                    state.error =
                        action.payload ||
                        action.error?.message ||
                        'Failed to load workload data'
                }
            )
            .addCase(fetchWorkloadProjectTimelog.pending, (state) => {
                state.detail.status = 'loading'
                state.detail.error = ''
            })
            .addCase(fetchWorkloadProjectTimelog.fulfilled, (state, action) => {
                state.detail.status = 'loaded'
                const list = Array.isArray(action.payload?.data)
                    ? action.payload.data
                    : []
                state.detail.rows = list
            })
            .addCase(fetchWorkloadProjectTimelog.rejected, (state, action) => {
                state.detail.status = 'error'
                state.detail.error =
                    action.payload ||
                    action.error?.message ||
                    'Failed to load timelog details'
            })
            .addCase(fetchWorkloadScreenshots.pending, (state, action) => {
                if (!state.detail.screenshots) {
                    state.detail.screenshots = { ...initialScreenshotsState }
                }
                state.detail.screenshots.status =
                    action.meta?.arg?.reset === false
                        ? 'loadingMore'
                        : 'loading'
                state.detail.screenshots.error = ''
            })
            .addCase(fetchWorkloadScreenshots.fulfilled, (state, action) => {
                if (!state.detail.screenshots) {
                    state.detail.screenshots = { ...initialScreenshotsState }
                }
                state.detail.screenshots.status = 'loaded'
                const { reset, data } = action.payload ?? {}
                const list = Array.isArray(data?.data) ? data.data : []
                state.detail.screenshots.rows = reset
                    ? list
                    : [...state.detail.screenshots.rows, ...list]
                state.detail.screenshots.skip =
                    state.detail.screenshots.rows.length
                state.detail.screenshots.isNext = Boolean(data?.isNext)
            })
            .addCase(fetchWorkloadScreenshots.rejected, (state, action) => {
                if (!state.detail.screenshots) {
                    state.detail.screenshots = { ...initialScreenshotsState }
                }
                state.detail.screenshots.status = 'error'
                state.detail.screenshots.error =
                    action.payload ||
                    action.error?.message ||
                    'Failed to load screenshots'
            })
    },
})

export const {
    setWorkloadFilters,
    openWorkloadTimelogDrawer,
    closeWorkloadTimelogDrawer,
    resetWorkloadState,
} = workloadSlice.actions
export const workloadState = (state) => state.workloadSlice || initialState
export default workloadSlice.reducer
